<template lang="pug">
.outing-list-tab(v-cloak)
  OutingTable.mt-3(
    v-model="items"
    :fields="fields"
    :totalRows.sync="_rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    :allSelect.sync="allSelects[Number(currentPage) - 1]"
    @onDetail="onDetail"
    @onConsented="onConsented"
    @onRefuse="onRefuse"
    @onCancel="onCancel"
    @onAllSelect="onAllSelect"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    //- 一括承諾
    .flex-fill
      button.btn.multiple-consented-btn#multiple-consented-btn(@click="onMultipleConsented" :class="{ disable: disableMultipleConsented }") 一括承諾
      b-popover(target="multiple-consented-btn" triggers="hover")
        .d-flex.flex-column
          span チェックを付けた外出外泊届けを一括で承諾することができます。一番上のチェックを選択すると、全件にチェックを付けたり外したりできます。
    //- 削除済みを表示
    .deleted-filter.mr-2
      b-form-checkbox(v-model="filter.showCanceled") キャンセル済を表示
    //- 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="outing-list-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・利用者名
          div ・利用者名（ふりがな）※
          div ・部屋名
          div ・申請者名
          div ・お迎えに来る方
          div ※ 利用者情報にふりがなを登録前の外出外泊届けはふりがな検索ができません。
    //- フィルタリング
    .resident-group-filter.d-flex.align-items-center
      span.table-option-text 利用者グループ:
      .filter
        b-form-select(id="outing-filter-input" v-model="filter.id" :options="filterOptions" size="md")
    //- ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="outing-list-per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    //- ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="_rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import SystemError from "@/commons/models/SystemError";

import Setting from "../../../../public/setting.json";

import OutingsService, { OutingGetModel } from "@/commons/services/Outings";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import OutingTable, { OutingTableField, OutingTableItem, FilterSetting } from "@/pc/containers/outings/OutingTable.vue";

/**
 * ### 外出外泊届け一覧コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OutingTable
  }
})
export default class OutingListTab extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** テーブル列名 */
  fields: Array<OutingTableField> = [];
  /** テーブル各行の情報 */
  items: Array<OutingTableItem> = [];
  /** テーブルの「選択」列のチェックボックス値 */
  allSelects: boolean[] = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", id: "", showCanceled: false };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.outings.list.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.outings.list.pageOptions;

  /** 一括承諾ボタンの非活性 */
  disableMultipleConsented: boolean = true;

  /**
   * ### 一括承諾ボタンの活性／非活性を切り替えるためにitemsを監視する
   */
  @Watch("items", { deep: true })
  changeItems(newValue: Array<OutingTableItem>): void {
    for (const item of newValue) {
      if (item.select == true) {
        this.disableMultipleConsented = false;
        return;
      }
    }
    this.disableMultipleConsented = true;
    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 行数を返す
   *
   * - キャンセル済みを表示する場合は全体件数を返す
   * - キャンセル済みを表示しない場合は削除されていない行の件数を返す
   */
  get _rows(): number {
    let rows = 0;
    if (this.filter.showCanceled == true) {
      return this.rows;
    } else {
      for (const item of this.items) {
        if (item.deleteFlag == false) {
          rows++;
        }
      }
      return rows;
    }
  }
  //** setterがないと警告が出るので対応 */
  set _rows(newValue: number) {
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `outing-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<OutingTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    await this.createView();
    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<OutingTableField> {
    const fields: Array<OutingTableField> = [];
    fields.push({ key: "select", label: "", sortable: false, sortDirection: "desc", class: "select text-center" });
    fields.push({ key: "type", label: "種別", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "inputBy", label: "入力", sortable: false, sortDirection: "desc", class: "input-by text-center" });
    fields.push({ key: "status", label: "状態", sortable: false, sortDirection: "desc", class: "status text-center" });
    fields.push({ key: "datetimes", label: "日時", sortable: false, sortDirection: "desc", class: "datetimes" });
    fields.push({ key: "resident", label: "", sortable: false, sortDirection: "desc", class: "resident" });
    fields.push({ key: "applicant", label: "", sortable: false, sortDirection: "desc", class: "applicant" });
    fields.push({ key: "detail", label: "届出内容", sortable: false, sortDirection: "desc", class: "detail text-center" });
    fields.push({ key: "control", label: "操作", sortable: false, sortDirection: "desc", class: "control text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });
      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["outing-list-per-page"]);
      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 現在より未来の外出外泊届けを取得する
    // 開始日時は今日の0時、終了日時は17桁の90000101000000000でリクエストする
    const startDatetime: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
    const endDatetime: number = 90000101000000000;

    // 全体の行数を非同期で取得する
    await this.setRows(startDatetime, endDatetime);

    // テーブルに渡す各プロパティを作成する
    const outings: Array<OutingGetModel> = await this.getOutings(startDatetime, endDatetime);
    const items: Array<OutingTableItem> = this.createItems(outings);
    this.items = items;
    this.rows = this._rows;

    // 現在ページがstoreに存在する場合は復元する
    await this.$timerUtil.setTimeout(500); // b-paginationの:total-rows=_rows()が実行されるのに時差があるので0.5秒待つ
    if (this.$options.name && itemListFilterStore.currentPageMap[this.$options.name] != undefined) {
      this.currentPage = itemListFilterStore.currentPageMap[this.$options.name];
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(start: number, end: number) {
    try {
      const count: number = await this.outingsService.getCount(userStore, { start, end });
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 外出外泊届けを取得する
   */
  public async getOutings(start: number, end: number): Promise<Array<OutingGetModel>> {
    let response: Array<OutingGetModel> = [];

    try {
      response = await this.outingsService.gets(userStore, { start, end });
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.outingsService.getCount(userStore, { start, end });
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { start: number; end: number; startIndex?: number; endIndex?: number } = {
            start,
            end,
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.outingsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(outings: Array<OutingGetModel>): Array<OutingTableItem> {
    const items: Array<OutingTableItem> = [];
    for (const outing of outings) {
      items.push({
        ...outing,
        select: false,
        /** 行の設定 */
        _rowVariant: outing.deleteFlag ? "gray-dark" : undefined
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 全て選択を反映する
   */
  public async onAllSelect(item: OutingTableItem): Promise<void> {
    const start: number = (Number(this.currentPage) - 1) * Number(this.perPage);
    const end: number = Number(start) + Number(this.perPage);
    for (let i = start; i < end; i++) {
      if (this.items[i] && this.items[i].consented == false && this.items[i].deleteFlag == false) {
        this.items[i].select = this.allSelects[Number(this.currentPage) - 1];
      }
    }
    return;
  }

  /**
   * ### 詳細ボタン押下を親コンポーネントに通知する
   */
  public async onDetail(item: OutingTableItem): Promise<void> {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 承諾ボタン押下を親コンポーネントに通知する
   */
  public async onConsented(item: OutingTableItem): Promise<void> {
    this.$emit("onConsented", item);
    return;
  }

  /**
   * ### 辞退ボタン押下を親コンポーネントに通知する
   */
  public async onRefuse(item: OutingTableItem): Promise<void> {
    this.$emit("onRefuse", item);
    return;
  }

  /**
   * ### キャンセルボタン押下を親コンポーネントに通知する
   */
  public async onCancel(item: OutingTableItem): Promise<void> {
    this.$emit("onCancel", item);
    return;
  }

  /**
   * ### 一括承諾ボタン押下イベントを親コンポーネントに通知する
   */
  public onMultipleConsented(): void {
    if (this.disableMultipleConsented) {
      return;
    }

    let multipleConsentedTargets: Array<OutingTableItem> = [];
    for (const item of this.items) {
      if (item.select == true) {
        multipleConsentedTargets.push(item);
      }
    }
    this.$emit("onMultipleConsented", multipleConsentedTargets);
    return;
  }

  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
      itemListFilterStore.setCurrentPage({ url: this.$options.name, currentPage: this.currentPage });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
.outing-list-tab {
  .deleted-filter {
    min-width: 168px;
  }
  .word-search-filter {
    min-width: 336px;
  }
  .resident-group-filter {
    min-width: 295px;
  }
  .table-option-text {
    min-width: 113px;
  }
}
</style>
